import { useMemo } from 'react';
import * as THREE from 'three';
import { RoundedBox } from '@react-three/drei';

type Vec3 = [number, number, number];

const UP = new THREE.Vector3(0, 1, 0);

/** Plain box. */
export function Box({ args, mat, pos, rot }: { args: Vec3; mat: THREE.Material; pos: Vec3; rot?: Vec3 }) {
  return (
    <mesh position={pos} rotation={rot} material={mat}>
      <boxGeometry args={args} />
    </mesh>
  );
}

/** Rounded box. */
export function RBox({ args, radius = 0.05, smoothness = 4, mat, pos, rot }: { args: Vec3; radius?: number; smoothness?: number; mat: THREE.Material; pos: Vec3; rot?: Vec3 }) {
  return <RoundedBox args={args} radius={radius} smoothness={smoothness} position={pos} rotation={rot} material={mat} />;
}

/** Cylinder (args: [radiusTop, radiusBottom, height, radialSegments]). */
export function Cyl({ args, mat, pos, rot }: { args: [number, number, number, number?]; mat: THREE.Material; pos: Vec3; rot?: Vec3 }) {
  return (
    <mesh position={pos} rotation={rot} material={mat}>
      <cylinderGeometry args={[args[0], args[1], args[2], args[3] ?? 24]} />
    </mesh>
  );
}

/** Torus (args: [radius, tube, radialSeg, tubularSeg, arc?]). */
export function Tor({ args, mat, pos, rot }: { args: [number, number, number, number, number?]; mat: THREE.Material; pos: Vec3; rot?: Vec3 }) {
  return (
    <mesh position={pos} rotation={rot} material={mat}>
      <torusGeometry args={args} />
    </mesh>
  );
}

function orient(a: Vec3, b: Vec3) {
  const A = new THREE.Vector3(...a);
  const B = new THREE.Vector3(...b);
  const dir = new THREE.Vector3().subVectors(B, A);
  const len = dir.length();
  const mid = A.clone().add(B).multiplyScalar(0.5);
  const quat = new THREE.Quaternion().setFromUnitVectors(UP, dir.normalize());
  return { len, pos: mid.toArray() as Vec3, quat };
}

/** A cylinder spanning points a→b (its local +Y axis is aligned to the segment). */
export function Segment({ a, b, r, mat, segments = 8 }: { a: Vec3; b: Vec3; r: number; mat: THREE.Material; segments?: number }) {
  const { len, pos, quat } = useMemo(() => orient(a, b), [a, b]);
  return (
    <mesh position={pos} quaternion={quat} material={mat} castShadow>
      <cylinderGeometry args={[r, r, len, segments]} />
    </mesh>
  );
}

/** A rounded box spanning points a→b — used for chunky pillars/struts. */
export function BarSegment({ a, b, w, d, mat }: { a: Vec3; b: Vec3; w: number; d: number; mat: THREE.Material }) {
  const { len, pos, quat } = useMemo(() => orient(a, b), [a, b]);
  return (
    <RoundedBox args={[w, len, d]} radius={Math.min(w, d) * 0.4} smoothness={3} position={pos} quaternion={quat} material={mat} castShadow />
  );
}
